import { computed, ref, watch } from "vue";
import { defineStore } from "pinia";
import {
  fromMaybe,
  isJust,
  isNothing,
  matchMaybe,
  toMaybe,
} from "../lib/maybe";
import { joinProjectPath } from "../lib/runtime";
import {
  sceneLabel,
  sceneOpeningFromDoc,
  unknownOpening,
  type SceneLabel,
  type SceneOpening,
} from "../lib/sceneLabel";
import { readProjectFile } from "../lib/tauri/core";
import { sceneFilesInOrder, type ZwScene } from "../lib/zwConfig";
import { useEditorSessionStore } from "./useEditorSessionStore";

/**
 * Shared scene-text cache for the manuscript outline and the timeline.
 * Keyed by `Chapters/<folder>/<scene file>`. The open scene follows the live
 * buffer. Every other scene is read once. A successful save writes that body
 * back here, so opening the timeline does not read the manuscript again.
 *
 * Cloud sessions can't read sibling files yet (`readProjectFile` throws), so
 * a scene that was never opened stays `unknown` → "Untitled scene".
 */
export const useSceneOpeningsStore = defineStore("sceneOpenings", () => {
  const session = useEditorSessionStore();
  const stored = ref<Record<string, SceneOpening>>({});
  const documents = ref<Record<string, string>>({});

  /** Stable string key so a new manuscript array with the same paths does not
   * retrigger loads (ApplyPayload replaces projectConfig on every scene switch). */
  const scenePathsKey = computed(() =>
    sceneFilesInOrder(session.manuscript).join("\0"),
  );
  const scenePaths = computed(() =>
    scenePathsKey.value.length === 0 ? [] : scenePathsKey.value.split("\0"),
  );

  const liveOpening = computed((): SceneOpening =>
    sceneOpeningFromDoc(session.doc),
  );

  function writeOpening(path: string, opening: SceneOpening): void {
    const previous = toMaybe(stored.value[path]);
    if (
      isJust(previous) &&
      previous.value.kind === opening.kind &&
      (opening.kind !== "text" ||
        (previous.value.kind === "text" &&
          previous.value.text === opening.text))
    ) {
      return;
    }
    stored.value = { ...stored.value, [path]: opening };
  }

  async function loadPath(path: string, root: string): Promise<void> {
    try {
      const raw = await readProjectFile(joinProjectPath(root, path));
      if (fromMaybe(session.rootPath) !== root) return;
      documents.value = { ...documents.value, [path]: raw };
      writeOpening(path, sceneOpeningFromDoc(raw));
    } catch {
      if (fromMaybe(session.rootPath) !== root) return;
      // Keep a prior good opening if disk read fails mid-switch.
      if (path in stored.value === false) {
        writeOpening(path, unknownOpening);
      }
    }
  }

  async function loadMissing(): Promise<void> {
    if (isNothing(session.rootPath)) return;
    const root = session.rootPath.value;
    const missing = scenePaths.value.filter(
      (path) => path in stored.value === false,
    );
    if (missing.length === 0) return;
    await Promise.all(missing.map((path) => loadPath(path, root)));
  }

  /** Watch the path string, not the Maybe wrapper. ApplyPayload rebuilds
   * `just(root)` on every scene switch; clearing on identity churn wiped the
   * cache, flashed synopsis placeholders (shorter rows), and the outline
   * scrollTop stayed at the dipped value after heights recovered. */
  watch(
    () => fromMaybe(session.rootPath) ?? null,
    () => {
      stored.value = {};
      documents.value = {};
      void loadMissing();
    },
  );
  watch(scenePathsKey, () => void loadMissing(), { immediate: true });

  watch(
    () => session.doc,
    (raw) => {
      if (isJust(session.outlineActiveFile)) {
        documents.value = {
          ...documents.value,
          [session.outlineActiveFile.value]: raw,
        };
      }
    },
    { flush: "sync" },
  );

  const documentsByPath = computed(() =>
    isJust(session.outlineActiveFile)
      ? { ...documents.value, [session.outlineActiveFile.value]: session.doc }
      : documents.value,
  );

  // Mirror the live buffer into the cache while a scene is open, so leaving
  // it keeps the text the outline and the timeline already showed.
  watch(
    liveOpening,
    (opening) => {
      matchMaybe(session.outlineActiveFile, {
        just: (path) => {
          writeOpening(path, opening);
        },
        nothing: () => {},
      });
    },
    { flush: "sync" },
  );

  /** Record a scene body that just landed on disk. */
  function noteSaved(path: string, content: string): void {
    documents.value = { ...documents.value, [path]: content };
    writeOpening(path, sceneOpeningFromDoc(content));
  }

  function openingFor(path: string): SceneOpening {
    return matchMaybe(session.outlineActiveFile, {
      just: (active) =>
        active === path
          ? liveOpening.value
          : (stored.value[path] ?? unknownOpening),
      nothing: () => stored.value[path] ?? unknownOpening,
    });
  }

  function labelFor(scene: ZwScene, path: string): SceneLabel {
    return sceneLabel(scene, openingFor(path));
  }

  /**
   * Reactive snapshot of cached openings. Pickers that build option lists in a
   * `computed` must read this (or a key from it); calling `openingFor` alone
   * does not always re-run that computed when async loads finish.
   */
  const openingsByPath = computed(() => stored.value);

  /** Kick a load if paths are still missing (safe to call from pickers). */
  function ensureLoaded(): void {
    void loadMissing();
  }

  return {
    openingFor,
    labelFor,
    ready: computed(() =>
      scenePaths.value.every((path) => path in stored.value),
    ),
    openingsByPath,
    documentsByPath,
    ensureLoaded,
    noteSaved,
  };
});
